/// <reference types="google.maps" />
import { Injectable, Renderer2, RendererFactory2, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IApiAddAddressInput } from '../graphql/types/types';
import { AuthService } from '../../services';

interface GoogleMapsWindow extends Window {
  google?: typeof google;
}

@Injectable()
export class GoogleMapService {

  // read google api key from auth service
  private API_KEY = "";
  private renderer: Renderer2;

  constructor(rendererFactory: RendererFactory2, private _httpClient: HttpClient, @Inject(DOCUMENT) private document, private authService: AuthService) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.authService.getGoogleMapId.subscribe((key) => {
      this.API_KEY = key;
    })
  }

  public loadGoogleMapApi() {
    const googleMapsWindow: GoogleMapsWindow = window;
    if (!googleMapsWindow.google) return from(new Promise((resolve, reject) => {
      const script = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.API_KEY}&libraries=places,marker`;
      script.text = ``;
      script.onload = resolve;
      script.onerror = reject;
      this.renderer.appendChild(this.document.head, script);
    }));
    return of(0);
  }

  public fetchGeoCoordinates(address: IApiAddAddressInput) {
    // todo: this works for our purposes, but could be hardened a little
    // geocoding api will return multiple valid locations
    const mapsUri = 'https://maps.googleapis.com/maps/api/geocode/json?address=';
    const headers: HttpHeaders = new HttpHeaders({ Accept: 'application/json' });
    return this._httpClient.get(
      `${mapsUri}${this._formatAddress(address)}${'&key='}${this.API_KEY}`,
      { headers: headers }
    ).pipe(
      map((data: any) => {
        const status = data.status;
        const results: google.maps.GeocoderResult = data.results;
        if (status === "OK") return results[0].geometry.location;
        throw new Error('Not a valid location, according to Google');
      })
    );
  }

  public getTimezone(latitude, longitude) {
    const mapUri = `https://maps.googleapis.com/maps/api/timezone/json?location=${latitude}%2C${longitude}&timestamp=${Date.now() / 1000}&key=${this.API_KEY}`;
    const headers: HttpHeaders = new HttpHeaders({ Accept: 'application/json' });
    return this._httpClient.get(
      mapUri,
      { headers: headers }
    ).pipe(
      map((data: any) => {
        const status = data.status;
        const results: google.maps.GeocoderResult = data.results;
        if (status === "OK") return data.timeZoneId;
        throw new Error('Not a valid location, according to Google');
      })
    );
  }

  public getAddressByZip(zip) {
    const mapUri = `https://maps.googleapis.com/maps/api/geocode/json?address=${zip}&key=${this.API_KEY}`;
    const headers: HttpHeaders = new HttpHeaders({ Accept: 'application/json' });
    return this._httpClient.get(
      mapUri,
      { headers: headers }
    ).pipe(
      map((data: any) => {
        const status = data.status;
        const results: google.maps.GeocoderResult = data.results;
        if (status === "OK") return data.results;
        throw new Error('Not a valid location, according to Google');
      })
    );
  }

  public validatePostalCode(zip) {
    const mapUri = `https://maps.googleapis.com/maps/api/geocode/json?address=${zip}&key=${this.API_KEY}&components=country:US`;
    const headers: HttpHeaders = new HttpHeaders({ Accept: 'application/json' });
    return this._httpClient.get(
      mapUri,
      { headers: headers }
    ).pipe(
      map((data: any) => {
        const status = data.status;
        const results: google.maps.GeocoderResult[] = data.results;
        const result = results?.find(r => r?.types?.includes('postal_code'));
        if (result && status === "OK") {
          return result;
        } else {
          return false;
        }
      })
    );
  }

  public getDistance(origins: any, destinations: any) {
    const service = new google.maps.DistanceMatrixService();
    const originsArr = Array.isArray(origins) ? origins : [origins];
    const destinationsArr = typeof destinations === 'string' ? destinations.split('|') : (Array.isArray(destinations) ? destinations : [destinations]);

    return from(new Promise((resolve, reject) => {
      service.getDistanceMatrix({
        origins: originsArr,
        destinations: destinationsArr,
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.IMPERIAL
      }, (response, status) => {
        if (status === google.maps.DistanceMatrixStatus.OK && response) {
          const elements = response.rows?.[0]?.elements || [];
          if (elements.length === 1) {
            resolve({
              distance: elements[0]?.distance?.value * 0.000621371 || 0,
              duration: elements[0]?.duration?.text
            });
          } else {
            resolve(elements.map(el => ({
              distance: el?.distance?.value * 0.000621371 || 0,
              duration: el?.duration?.text
            })));
          }
        } else {
          reject(new Error(`Distance Matrix failed: ${status}`));
        }
      });
    }));
  }

  private _formatAddress(address: IApiAddAddressInput) {
    const reducer = (accumulator, currentValue) => (currentValue) ? accumulator + currentValue + ' ' : accumulator;
    return Object.values(address).reduce(reducer, '').trim(' ');
  }

}
